import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, format } from 'date-fns';
import {
  ListChecks, CalendarClock, Layers, LayoutTemplate, Filter, ClipboardCheck,
  ChevronLeft, ChevronRight, Loader2, ArrowLeft, Globe2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { EditorToolSidebar } from '@/components/editor/EditorToolSidebar';
import { EditorTemplatesPanel } from '@/components/editor/EditorTemplatesPanel';
import { ScheduleCalendarGrid, groupPostsByDay } from '@/components/schedule/ScheduleCalendarGrid';
import { ScheduleQueuePanel } from '@/components/schedule/ScheduleQueuePanel';
import { ScheduleOneOffPanel } from '@/components/schedule/ScheduleOneOffPanel';
import { ScheduleBatchPanel } from '@/components/schedule/ScheduleBatchPanel';
import { ScheduleFiltersPanel } from '@/components/schedule/ScheduleFiltersPanel';
import { SchedulePreviewPanel } from '@/components/schedule/SchedulePreviewPanel';
import { PLATFORMS } from '@/lib/clipPlatformFormats';
import { getAllVariants } from '@/lib/optimizeVariants';
import {
  getQueueGroups, getScheduledPosts, schedulePost, updateScheduledPost, removeScheduledPost, duplicateScheduledPost,
  fillNextAvailableSlots, getCreatorTimezone, setCreatorTimezone, COMMON_TIMEZONES, publishNow, getPlatformConnections, resolvePublishAssetUrl,
} from '@/lib/scheduleData';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';

const API_URL = window.__BACKEND_URL;

const TOOL_CATEGORIES = [
  { key: 'queue', label: 'Content Queue', icon: ListChecks },
  { key: 'scheduling', label: 'Scheduling', icon: CalendarClock },
  { key: 'batch', label: 'Batch', icon: Layers },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'filters', label: 'Filters', icon: Filter },
  { key: 'review', label: 'Review', icon: ClipboardCheck },
];
const VIEWS = ['month', 'week', 'day', 'agenda'];

/**
 * Schedule — a calendar-first Production Tool, separate from Optimize
 * (section 2). Same editor-shell sidebar pattern as Optimize/Images &
 * Posts/Clips, but the CENTER area is the calendar itself (section 3),
 * never a second metadata form — metadata editing stays in Optimize.
 */
export default function ScheduleWorkspace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const creatorIdParam = searchParams.get('creatorId');

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const creatorId = project?.creator_id || creatorIdParam;

  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scope, setScope] = useState(projectId ? 'project' : 'creator');
  const [platformFilter, setPlatformFilter] = useState(new Set(PLATFORMS.map((p) => p.key)));
  const [statusFilter, setStatusFilter] = useState(new Set(['scheduled', 'publishing', 'published', 'failed']));
  const [kindFilter, setKindFilter] = useState(new Set(['video', 'clip', 'design']));

  const [toolCategory, setToolCategory] = useState('queue');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [refreshTick, setRefreshTick] = useState(0);
  const bump = () => setRefreshTick((t) => t + 1);

  const [selectedItem, setSelectedItem] = useState(null); // { variant, platform }
  const [oneOffDate, setOneOffDate] = useState(addDays(new Date(), 1));
  const [oneOffTime, setOneOffTime] = useState('10:00');
  const [publishing, setPublishing] = useState(false);

  const [selectedPost, setSelectedPost] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [batchMode, setBatchMode] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState([]); // [{variant, platform}]
  const [batchDateRange, setBatchDateRange] = useState({ start: new Date(), end: addDays(new Date(), 14) });
  const [postingDays, setPostingDays] = useState(['mon', 'wed', 'fri']);
  const [postingTimes, setPostingTimes] = useState(['10:00']);
  const [filling, setFilling] = useState(false);
  const [fillResult, setFillResult] = useState(null); // { proposals, skipped }

  const [pendingReschedule, setPendingReschedule] = useState(null); // { postId, newDate }
  const [connections, setConnections] = useState([]);
  const [timezone, setTimezone] = useState(() => getCreatorTimezone(creatorIdParam));

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}`, { credentials: 'include' });
        if (res.ok) setProject((await res.json()).project);
      } catch { toast.error('Could not load this project'); }
      setLoading(false);
    })();
  }, [projectId]);

  useEffect(() => { if (creatorId) { setTimezone(getCreatorTimezone(creatorId)); getPlatformConnections({ apiUrl: API_URL, creatorId }).then(setConnections); } }, [creatorId]);

  const queueGroups = useMemo(() => getQueueGroups({ projectId, creatorId, scope }), [projectId, creatorId, scope, refreshTick]);
  const allScopedPosts = useMemo(() => getScheduledPosts({ projectId, creatorId, scope }), [projectId, creatorId, scope, refreshTick]);
  const visiblePosts = useMemo(() => allScopedPosts.filter((p) => platformFilter.has(p.platform) && statusFilter.has(p.status || 'scheduled') && (!p.contentKind || kindFilter.has(p.contentKind))), [allScopedPosts, platformFilter, statusFilter, kindFilter]);
  const postsByDay = useMemo(() => groupPostsByDay(visiblePosts), [visiblePosts]);

  const jumpTo = (category) => { setToolCategory(category); setSidebarCollapsed(false); };

  const navigateDate = (dir) => {
    if (view === 'month') setCurrentDate((d) => (dir > 0 ? addMonths(d, 1) : subMonths(d, 1)));
    else if (view === 'week') setCurrentDate((d) => (dir > 0 ? addWeeks(d, 1) : subWeeks(d, 1)));
    else setCurrentDate((d) => (dir > 0 ? addDays(d, 1) : subDays(d, 1)));
  };

  const rangeLabel = view === 'month' ? format(currentDate, 'MMMM yyyy') : view === 'week' ? `Week of ${format(currentDate, 'MMM d, yyyy')}` : view === 'day' ? format(currentDate, 'MMMM d, yyyy') : 'Next 30 days';

  // ── One-off scheduling (section 6) ──
  const scheduleOneItem = (variant, platform) => { setSelectedItem({ variant, platform }); jumpTo('scheduling'); };
  const confirmOneOff = () => {
    if (!selectedItem || !oneOffDate || !oneOffTime) return;
    const [h, m] = oneOffTime.split(':').map(Number);
    const dt = new Date(oneOffDate); dt.setHours(h, m || 0, 0, 0);
    schedulePost({ variant: selectedItem.variant, platform: selectedItem.platform, scheduledAt: dt.toISOString(), timezone });
    toast.success(`Scheduled for ${format(dt, 'PPP p')}.`);
    setSelectedItem(null);
    bump();
    jumpTo('review');
  };

  const hasConnectionFor = (platform) => connections.some((c) => c.platform === platform && c.is_active);
  const handlePublishNow = async () => {
    if (!selectedItem) return;
    const connection = connections.find((c) => c.platform === selectedItem.platform && c.is_active);
    if (!connection) { toast.error(`Connect ${selectedItem.platform} first.`); return; }
    setPublishing(true);
    try {
      const asset = await resolvePublishAssetUrl({ apiUrl: API_URL, variant: selectedItem.variant });
      if (!asset.ok) { toast.error(asset.reason); return; }
      const result = await publishNow({ apiUrl: API_URL, connectionId: connection.id, assetUrl: asset.url, caption: selectedItem.variant.description || '' });
      if (!result.ok) { toast.error(result.reason || 'Publish Now was blocked.'); return; }
      const post = schedulePost({ variant: selectedItem.variant, platform: selectedItem.platform, scheduledAt: new Date().toISOString(), timezone });
      updateScheduledPost(post.id, { status: 'published', mocked: result.mocked });
      toast.success(result.mocked ? 'Sent through the mock posting pipeline — marked Published (Mock). No real post was made on the platform.' : 'Published.');
      setSelectedItem(null);
      bump();
    } catch { toast.error('Publish Now failed to reach the server.'); }
    setPublishing(false);
  };

  // ── Calendar interactions ──
  const openPost = (post) => { setSelectedPost(post); setPreviewOpen(true); };
  const handleEditDateTime = (postId, iso) => { updateScheduledPost(postId, { scheduledAt: iso }); bump(); toast.success('Rescheduled.'); setSelectedPost((p) => (p?.id === postId ? { ...p, scheduledAt: iso } : p)); };
  const handleDuplicate = (postId) => { duplicateScheduledPost(postId); bump(); toast.success('Duplicated — scheduled one day later.'); };
  const handleRemove = (postId) => { removeScheduledPost(postId); bump(); setPreviewOpen(false); toast.info('Removed — the content is back in your Content Queue.'); };
  const openInOptimize = (post) => navigate(`/app/create/optimize?projectId=${post.projectId || projectId || ''}&creatorId=${post.creatorId || creatorId || ''}&assetId=${post.assetId || ''}`);

  const handleSelectSlot = (day) => { setOneOffDate(day); jumpTo(selectedItem ? 'scheduling' : 'queue'); if (!selectedItem) toast.info('Pick a Content Queue item, then this date will carry over.'); };

  const handleDropPost = ({ queueItem, postId }, day) => {
    if (queueItem) {
      const variant = getAllVariants().find((v) => v.id === queueItem.variantId);
      if (!variant) return;
      const dt = new Date(day);
      if (dt.getHours() === 0 && dt.getMinutes() === 0) dt.setHours(10, 0, 0, 0); // day-level drop (Month/Week) with no time info — default to 10am; an hour-slot drop (Day view) already carries its own time
      const post = schedulePost({ variant, platform: queueItem.platform, scheduledAt: dt.toISOString(), timezone });
      bump();
      toast.success(`Scheduled for ${format(dt, 'PPP p')} — adjust the exact time in the preview if needed.`);
      openPost(post);
      return;
    }
    if (postId) {
      const existing = allScopedPosts.find((p) => p.id === postId);
      if (!existing) return;
      const old = new Date(existing.scheduledAt);
      const dayHasTime = day.getHours() !== 0 || day.getMinutes() !== 0;
      const next = new Date(day);
      if (!dayHasTime) next.setHours(old.getHours(), old.getMinutes(), 0, 0); // Month/Week drop carries only a date — keep the original time-of-day
      setPendingReschedule({ postId, newDate: next });
    }
  };
  const confirmReschedule = () => { if (!pendingReschedule) return; handleEditDateTime(pendingReschedule.postId, pendingReschedule.newDate.toISOString()); setPendingReschedule(null); };

  // ── Batch & Fill Next Available Slots (sections 7/8) ──
  const toggleBatchItem = (variant, platform) => {
    setSelectedBatch((prev) => (prev.some((i) => i.variant.id === variant.id) ? prev.filter((i) => i.variant.id !== variant.id) : [...prev, { variant, platform }]));
  };
  const removeFromBatch = (variantId) => setSelectedBatch((prev) => prev.filter((i) => i.variant.id !== variantId));
  const togglePostingDay = (day) => setPostingDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  const addPostingTime = (t) => setPostingTimes((prev) => (prev.includes(t) ? prev : [...prev, t].sort()));
  const removePostingTime = (t) => setPostingTimes((prev) => prev.filter((x) => x !== t));
  const previewFill = () => {
    setFilling(true);
    const { proposals, skipped } = fillNextAvailableSlots({ items: selectedBatch, startDate: batchDateRange.start, endDate: batchDateRange.end, postingDays, postingTimes, existingPosts: allScopedPosts });
    setFillResult({ proposals, skipped });
    setFilling(false);
    if (proposals.length === 0) toast.error('No valid slots found — widen the date range or add more posting days/times.');
  };
  const adjustProposalTime = (idx, localDateTimeValue) => {
    setFillResult((prev) => ({ ...prev, proposals: prev.proposals.map((p, i) => (i === idx ? { ...p, proposedAt: new Date(localDateTimeValue).toISOString() } : p)) }));
  };
  const confirmBatch = () => {
    fillResult.proposals.forEach((p) => schedulePost({ variant: p.variant, platform: p.platform, scheduledAt: p.proposedAt, timezone, batchId: `batch_${Date.now()}` }));
    toast.success(`Scheduled ${fillResult.proposals.length} post(s).`);
    setSelectedBatch([]); setFillResult(null); setBatchMode(false);
    bump();
    jumpTo('review');
  };

  // ── Scheduling Templates (section 9) — reuses the Template Folder architecture, never binds to specific content. ──
  const buildSchedulingTemplatePayload = () => ({
    scheduling: { platforms: Array.from(platformFilter), postingDays, postingTimes, timezone },
  });
  const applySchedulingTemplate = (tpl, editTpl) => {
    if (!editTpl.scheduling) { toast.error('This template has no reusable scheduling rules saved.'); return; }
    const s = editTpl.scheduling;
    if (s.postingDays) setPostingDays(s.postingDays);
    if (s.postingTimes) setPostingTimes(s.postingTimes);
    if (s.timezone) setTimezone(s.timezone);
    toast.success(`Applied "${editTpl.name}" — pick your content in the Content Queue, then Fill Next Available Slots.`);
    jumpTo('batch');
  };

  if (loading) return <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center" data-testid="schedule-loading"><Loader2 className="h-6 w-6 text-[#3A7BFF] animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white pb-10" data-testid="schedule-workspace">
      <div className="container mx-auto p-6 max-w-[1700px]">
        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-[#8A93A6] mb-2">
              <Link to="/app/create" className="hover:text-white">Create</Link><span className="opacity-50">/</span>
              {projectId && <><Link to="/app/create/workspace" className="hover:text-white">Workspace</Link><span className="opacity-50">/</span>
                <Link to={`/app/create/workspace/project/${projectId}`} className="hover:text-white" data-testid="schedule-back-to-project-link">{project?.name || 'Project'}</Link><span className="opacity-50">/</span></>}
              <span className="text-[#C0C7D4]">Schedule</span>
            </div>
            <h1 className="text-2xl font-bold" data-testid="schedule-title">Schedule</h1>
          </div>
          <div className="flex items-center gap-2">
            <ProductionToolsDropdown currentTool="schedule" creatorId={creatorId} projectId={projectId} />
            <Globe2 className="h-3.5 w-3.5 text-[#555E6E]" />
            <Select value={timezone} onValueChange={(tz) => { setTimezone(tz); setCreatorTimezone(creatorId, tz); }}>
              <SelectTrigger className="h-8 w-[190px] text-xs bg-white/5 border-white/10" data-testid="schedule-timezone-select"><SelectValue /></SelectTrigger>
              <SelectContent>{COMMON_TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
            </Select>
            {projectId && <Button variant="outline" size="sm" className="border-white/10" onClick={() => navigate(`/app/create/optimize?projectId=${projectId}&creatorId=${creatorId || ''}`)} data-testid="schedule-go-to-optimize-button">Go to Optimize</Button>}
            <Button variant="outline" size="sm" className="border-white/10" onClick={() => navigate(projectId ? `/app/create/workspace/project/${projectId}` : '/app/create/workspace')} data-testid="schedule-back-button"><ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Back</Button>
          </div>
        </div>

        <div className="flex gap-3 items-stretch">
          <EditorToolSidebar categories={TOOL_CATEGORIES} activeCategory={toolCategory} onSelectCategory={setToolCategory} collapsed={sidebarCollapsed} onToggleCollapsed={() => setSidebarCollapsed((c) => !c)} />

          {!sidebarCollapsed && (
            <div className="w-80 shrink-0" data-testid="schedule-tool-panel">
              <div className="glass rounded-xl p-4 lg:h-[640px] overflow-y-auto" data-testid={`schedule-panel-${toolCategory}`}>
                {toolCategory === 'queue' && (
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">Content Queue</p>
                      <button onClick={() => setBatchMode((b) => !b)} className={`text-[11px] px-2 py-0.5 rounded-full border ${batchMode ? 'border-[#3A7BFF] text-[#3A7BFF]' : 'border-white/10 text-[#8A93A6]'}`} data-testid="schedule-batch-mode-toggle">{batchMode ? 'Batch mode: On' : 'Batch mode: Off'}</button>
                    </div>
                    <ScheduleQueuePanel groups={queueGroups} onScheduleOne={scheduleOneItem} batchMode={batchMode} selectedBatchKeys={new Set(selectedBatch.map((i) => i.variant.id))} onToggleBatchItem={toggleBatchItem} />
                  </div>
                )}
                {toolCategory === 'scheduling' && (
                  <ScheduleOneOffPanel item={selectedItem} date={oneOffDate} time={oneOffTime} onDateChange={setOneOffDate} onTimeChange={setOneOffTime} onConfirm={confirmOneOff} onPublishNow={handlePublishNow} publishing={publishing} hasConnection={selectedItem ? hasConnectionFor(selectedItem.platform) : false} publishAssetAvailable={selectedItem ? selectedItem.variant.contentKind === 'video' : false} disabled={!selectedItem} />
                )}
                {toolCategory === 'batch' && (
                  <ScheduleBatchPanel
                    selectedBatch={selectedBatch} onRemoveFromBatch={removeFromBatch}
                    dateRange={batchDateRange} onDateRangeChange={setBatchDateRange}
                    postingDays={postingDays} onTogglePostingDay={togglePostingDay}
                    postingTimes={postingTimes} onAddPostingTime={addPostingTime} onRemovePostingTime={removePostingTime}
                    onPreviewFill={previewFill} filling={filling} proposals={fillResult?.proposals || null} skipped={fillResult?.skipped || []}
                    onAdjustProposalTime={adjustProposalTime} onConfirmBatch={confirmBatch} onCancelPreview={() => setFillResult(null)}
                  />
                )}
                {toolCategory === 'templates' && (
                  <EditorTemplatesPanel creatorId={project?.creator_id} templateKind="Scheduling Template" filterTemplate={(et) => !!et.scheduling} buildTemplatePayload={buildSchedulingTemplatePayload} onApply={applySchedulingTemplate} applyHint="Set your posting days, times and platforms in Batch, then save it here as a reusable Scheduling Template." projectId={projectId} />
                )}
                {toolCategory === 'filters' && (
                  <ScheduleFiltersPanel scope={scope} onScopeChange={setScope} canUseCreatorScope={!!creatorId} platformFilter={platformFilter} onTogglePlatform={(p) => setPlatformFilter((prev) => { const n = new Set(prev); n.has(p) ? n.delete(p) : n.add(p); return n; })} statusFilter={statusFilter} onToggleStatus={(s) => setStatusFilter((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; })} kindFilter={kindFilter} onToggleKind={(k) => setKindFilter((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; })} />
                )}
                {toolCategory === 'review' && (
                  <SchedulePreviewPanel post={selectedPost} onEditDateTime={handleEditDateTime} onDuplicate={handleDuplicate} onRemove={handleRemove} onOpenInOptimize={openInOptimize} timezone={timezone} />
                )}
              </div>
            </div>
          )}

          {/* Center — the dominant calendar workspace */}
          <div className="flex-1 min-w-0">
            <div className="glass rounded-xl p-4 lg:h-[640px] overflow-y-auto" data-testid="schedule-calendar-workspace">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-7 w-7 border-white/10" onClick={() => navigateDate(-1)} data-testid="schedule-nav-prev"><ChevronLeft className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="sm" className="h-7 border-white/10 text-xs" onClick={() => setCurrentDate(new Date())} data-testid="schedule-nav-today">Today</Button>
                  <Button variant="outline" size="icon" className="h-7 w-7 border-white/10" onClick={() => navigateDate(1)} data-testid="schedule-nav-next"><ChevronRight className="h-3.5 w-3.5" /></Button>
                  <span className="text-sm font-medium ml-1" data-testid="schedule-range-label">{rangeLabel}</span>
                </div>
                <div className="flex items-center gap-1 bg-white/5 rounded-full p-0.5">
                  {VIEWS.map((v) => (
                    <button key={v} onClick={() => setView(v)} className={`text-xs px-2.5 py-1 rounded-full capitalize ${view === v ? 'bg-[#3A7BFF] text-white' : 'text-[#8A93A6] hover:text-white'}`} data-testid={`schedule-view-${v}`}>{v}</button>
                  ))}
                </div>
              </div>
              <ScheduleCalendarGrid view={view} currentDate={currentDate} postsByDay={postsByDay} onSelectPost={openPost} onSelectSlot={handleSelectSlot} onDropPost={handleDropPost} />
            </div>
          </div>
        </div>
      </div>

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="bg-[#0F1116] border-white/10 text-white overflow-y-auto" data-testid="schedule-preview-drawer">
          <SchedulePreviewPanel post={selectedPost} onEditDateTime={handleEditDateTime} onDuplicate={handleDuplicate} onRemove={handleRemove} onOpenInOptimize={openInOptimize} timezone={timezone} />
        </SheetContent>
      </Sheet>

      <Dialog open={!!pendingReschedule} onOpenChange={(o) => !o && setPendingReschedule(null)}>
        <DialogContent className="bg-[#0F1116] border-white/10 text-white" data-testid="schedule-reschedule-dialog">
          <DialogHeader><DialogTitle>Reschedule this post?</DialogTitle></DialogHeader>
          {pendingReschedule && <p className="text-sm text-[#8A93A6]">Move to <span className="text-white">{format(pendingReschedule.newDate, 'PPP p')}</span>?</p>}
          <DialogFooter>
            <Button variant="outline" className="border-white/10" onClick={() => setPendingReschedule(null)} data-testid="schedule-reschedule-cancel">Cancel</Button>
            <Button className="bg-[#3A7BFF]" onClick={confirmReschedule} data-testid="schedule-reschedule-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
